import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SignaturePadWithRef, SignaturePadRef } from '../SignaturePad';
import { signByToken, TBMAttendee } from '../../lib/tbm';
import { useOptionalLocale } from '../../lib/i18n/useLocale';
import { Button, TextButton } from '@safetyhub/ui';

interface DirectSignModalProps {
  qrToken: string;
  source: 'manager_proxy' | 'educator_proxy';
  onClose: () => void;
  onSigned: (attendee: TBMAttendee) => void;
}

export default function DirectSignModal({
  qrToken,
  source,
  onClose,
  onSigned,
}: DirectSignModalProps) {
  const [name, setName] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [completedSignatures, setCompletedSignatures] = useState<Array<{ name: string; signedAt: string }>>([]);
  const signaturePadRef = useRef<SignaturePadRef | null>(null);
  const localeCtx = useOptionalLocale();

  const handleSave = async () => {
    if (!name.trim()) {
      setError('작업자 이름을 입력해주세요.');
      return;
    }
    if (!hasSignature || !signaturePadRef.current) {
      setError('서명을 입력해주세요.');
      return;
    }

    // 저장 시점에 캔버스에서 완성된 서명 캡처
    const signatureData = signaturePadRef.current.toDataURL();

    setIsSaving(true);
    setError('');
    try {
      const attendee = await signByToken(
        qrToken,
        name.trim(),
        { signatureData, signatureMethod: 'DRAWN', signatureSource: source, viewedLanguage: localeCtx?.locale },
        'TABLET',
      );
      setCompletedSignatures(prev => [
        ...prev,
        {
          name: name.trim(),
          signedAt: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      onSigned(attendee);
      // 다음 작업자를 위해 초기화
      setName('');
      setHasSignature(false);
      signaturePadRef.current?.clear();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || '서명 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-white flex flex-col">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-brand-600 text-white">
        <div>
          <h2 className="text-lg font-bold">직접 서명 받기</h2>
          <p className="text-sm text-brand-100">참석: {completedSignatures.length}명</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-brand-700 rounded-card transition-colors"
          aria-label="닫기"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* 본문 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-card p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* 작업자 이름 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">
            작업자 이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="이름을 입력하세요"
            className="w-full px-3 py-3 border border-line rounded-control text-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            autoFocus
          />
        </div>

        {/* 서명 패드 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">
            서명 <span className="text-red-500">*</span>
          </label>
          <div className="border-2 border-ink-300 rounded-card overflow-hidden bg-surface-soft">
            <SignaturePadWithRef
              ref={signaturePadRef}
              onSignatureChange={setHasSignature}
              height={200}
            />
          </div>
          <TextButton
            type="button"
            tone="neutral"
            onClick={() => {
              setHasSignature(false);
              signaturePadRef.current?.clear();
            }}
            className="mt-1 text-sm"
          >
            지우기
          </TextButton>
        </div>

        {/* 저장 버튼 */}
        <Button
          variant="primary"
          size="lg"
          className="w-full text-lg"
          onClick={handleSave}
          disabled={isSaving || !name.trim() || !hasSignature}
        >
          {isSaving ? '저장 중...' : '서명 저장 & 다음 작업자'}
        </Button>

        {/* 완료된 서명 목록 */}
        {completedSignatures.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-ink-700 mb-2">완료된 서명</h3>
            <div className="space-y-1">
              {completedSignatures.map((sig, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 px-3 bg-green-50 rounded-card">
                  <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-green-800 font-medium">{sig.name}</span>
                  <span className="text-xs text-green-600 ml-auto">({sig.signedAt})</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
